import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ListSupplierProductsInput,
  ListSupplierProductsResponse,
  SupplierProductRow,
  GetSupplierProductDetailInput,
  GetSupplierProductDetailResponse,
  SupplierForProduct,
  PriceBreak,
} from '@erp/rpc';

export type {
  SupplierProductRow,
  ListSupplierProductsResponse,
  GetSupplierProductDetailResponse,
  SupplierForProduct,
  PriceBreak,
};

export function useSupplierProducts(
  input: ListSupplierProductsInput,
  enabled = true,
) {
  return useQuery<ListSupplierProductsResponse>({
    ...orpc.procurement.supplierProducts.list.queryOptions({ input }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useInfiniteSupplierProducts(
  input: Omit<ListSupplierProductsInput, 'page'>,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['procurement', 'supplierProducts', 'infinite', input],
    queryFn: ({ pageParam }) =>
      rpcClient.procurement.supplierProducts.list({
        ...input,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.reduce((count, page) => count + page.rows.length, 0) <
      lastPage.totalRows
        ? pages.length + 1
        : undefined,
    enabled,
  });
}

export function useSupplierProductDetail(
  input: GetSupplierProductDetailInput,
  enabled = true,
) {
  return useQuery<GetSupplierProductDetailResponse>({
    ...orpc.procurement.supplierProducts.getByProduct.queryOptions({ input }),
    enabled: enabled && !!input.productV3Key,
  });
}
